import { Box, BoxProps, SxProps, Theme } from "@mui/material";
import { SPACER_SIZE } from "../global-config/BaseConfig";

/**
 * Definition of common types of spacers used across the app
 *
 * They all extend from a common style to ensure consistency
 *
 * Individual style modification is still possible via the SX prop and props will be passed
 *
 * It is preferred to define custom prop and handle the style modification internally if it is
 * not intended as a spot fix
 */

/**
 * Define props and a handler for common use cases (sx)
 */
type SxOverrides = {
	size?: number;
	vertical?: boolean;
	scale?: number;
};

const SxOverrideHandler = (sxDefault: SxProps<Theme>, sxOverrides?: SxOverrides) => {
	if (!sxOverrides) {
		return sxDefault;
	}

	let sx = { ...sxDefault };

	sx = sxOverrides?.size ? { ...sx, height: `${sxOverrides.size}px` } : sx;
	sx = sxOverrides?.vertical ? { ...sx, transform: "rotate(90deg)" } : sx;

	let scaledSize: number;
	if (sxOverrides?.scale) {
		scaledSize = sxOverrides?.size ? sxOverrides.size * sxOverrides.scale : parseInt(SPACER_SIZE) * sxOverrides.scale;
		sx = { ...sx, height: `${scaledSize}px` };
	}

	return sx;
};

/**
 * Additional props for functions internal logic
 */
type InternalLogic = {
	dummyLogic?: boolean;
};

/**
 * Define the type for all generic box props
 */
type GenericSpacerProps = SxOverrides & BoxProps & InternalLogic;

/* MUI Box with a growing flex display (Base Spacer) */
const sxSpacer: SxProps<Theme> = {
	display: "flex",
	width: SPACER_SIZE,
	height: SPACER_SIZE,
	boxSizing: "border-box",
};

export const Spacer = (props: GenericSpacerProps) => {
	const { size, vertical, scale, children, sx, ...rest } = props;
	const sxDefault = SxOverrideHandler(sxSpacer, { size, vertical, scale });

	return (
		<Box sx={{ ...sxDefault, ...sx }} {...rest}>
			{children}
		</Box>
	);
};
